import React, { useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import { useStore } from 'core/store';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import NotificationIcon from 'core/assets/svg/alert-white-bold.svg';
import { Label } from 'core';

import useStyles from './preview-footer.styles';

const formatDate = (dateStr) => {
  if (!dateStr) {
    return '';
  }
  return format(parseISO(dateStr), 'MM/dd/yyyy');
};

const formatDateTime = (dateStr) => {
  if (!dateStr) {
    return '';
  }
  return format(parseISO(dateStr), "MM/dd/yyyy '@' h:mm a 'EST'");
};

function PreviewFooter({ onSubmit, policyPreview }) {
  const classes = useStyles();
  const { policyId, id } = useParams();
  const {
    account: {
      policies: {
        list: policiesList,
        policy: { policyType }
      }
    }
  } = useStore();

  const isFarmPolicy = isFarmBasedPolicyType(policyType);

  const originalPolicy = policiesList.find((p) => p.id === policyId);
  const isExistingSegment = originalPolicy?.segments?.find(
    (s) => s.id === policyPreview?.changeDetails?.targetSegmentId
  );
  const changeType = isExistingSegment ? 'edit existing change' : 'create new change';

  useEffect(() => {
    window.onbeforeunload = () => true;
  }, []);

  const mainLabel = isFarmPolicy ? 'Keep making changes to this draft' : 'Changes were requested';
  const buildSubLabel = ({ changeType, changeDate, lastEdited, lastEditedBy }) => {
    return (
      <>
        This draft is set to <span>{changeType}</span> on <span>{changeDate}</span>. Last edited{' '}
        <span>{lastEdited}</span> by <span>{lastEditedBy}</span>.
      </>
    );
  };

  return (
    <div className={classNames(classes.container)}>
      <Grid container className={classNames(classes.footerInner)}>
        <div className={classNames(classes.footerItem)}>
          <Label className={classNames(classes.label)} type="subtitle1">
            {mainLabel}
          </Label>
          <Label className={classNames(classes.subLabel)} type="subtitle2">
            {buildSubLabel({
              changeType,
              changeDate: formatDate(policyPreview.changeDetails.effectiveDate),
              lastEdited: formatDateTime(policyPreview.changeDetails.updatedDateTime),
              lastEditedBy: policyPreview.createdBy.username
            })}
          </Label>
        </div>
        <Grid className={classNames(classes.footerItem, classes.last)}>
          <Button
            variant="contained"
            color="primary"
            data-cy="preview-policy-changes"
            onClick={onSubmit}
            className={classes.btn}
          >
            <Label className={classes.btnLabel}>Preview requested changes</Label>
          </Button>
        </Grid>
      </Grid>
      {isFarmPolicy && (
        <Grid className={classes.footerWarning}>
          <img src={NotificationIcon} className={classes.iconXSmall} alt="Notification Icon" />
          You are viewing a draft of this policy. View{' '}
          <a
            className={classes.link}
            href={`/customer/${id}/policy/${policyId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            the official policy here
          </a>{' '}
          or finish this draft for it to be live.
        </Grid>
      )}
    </div>
  );
}

PreviewFooter.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  policyPreview: PropTypes.object.isRequired
};

export default memo(PreviewFooter);
